import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Search, X, Check, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { matchesAll } from "@/lib/trFold";

/**
 * Bir tedarikçiye kumaş/aksesuar bağlamak için arama+seçim bileşeni.
 * Yeni: sağ üstte "+ Yeni Ekle" butonu — inline olarak yeni kumaş/aksesuar
 * oluşturur ve otomatik seçili yapar.
 */
export default function SupplierMaterialPicker({
  kind = "fabric",
  value = [],
  onChange,
  testIdPrefix,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newForm, setNewForm] = useState({ code: "", name: "", color: "", unit: "adet" });
  const [saving, setSaving] = useState(false);

  const label = kind === "fabric" ? "Kumaşlar" : "Aksesuarlar";
  const placeholder = kind === "fabric" ? "Kod veya isimle kumaş ara…" : "Kod veya isimle aksesuar ara…";
  const endpoint = kind === "fabric" ? "/inventory/fabrics" : "/inventory/accessories";
  const prefix = testIdPrefix || `supplier-${kind}-picker`;

  const load = () => {
    setLoading(true);
    api.get(endpoint)
      .then(({ data }) => setItems(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const selected = useMemo(() => items.filter((it) => value.includes(it.id)), [items, value]);

  const trLower = (s) => (s || "").toLocaleLowerCase("tr-TR");
  const filtered = useMemo(() => {
    const term = trLower(q.trim());
    if (!term) return items.slice(0, 30);
    return items
      .filter((it) => matchesAll(term, [it.code, it.name, it.color, it.category]))
      .slice(0, 30);
  }, [items, q]);

  const toggle = (id) => {
    if (value.includes(id)) onChange(value.filter((v) => v !== id));
    else onChange([...value, id]);
  };
  const remove = (id) => onChange(value.filter((v) => v !== id));

  const createNew = async () => {
    const nm = newForm.name.trim();
    if (!nm) return;
    setSaving(true);
    try {
      const payload = kind === "fabric"
        ? {
            code: newForm.code.trim() || `KUM-${Date.now().toString().slice(-6)}`,
            name: nm,
            color: newForm.color.trim() || "",
            unit: newForm.unit || "kg",
            stock_kg: 0,
            stock_metre: 0,
            purchase_price: 0,
          }
        : {
            code: newForm.code.trim() || `AKS-${Date.now().toString().slice(-6)}`,
            name: nm,
            unit: newForm.unit || "adet",
            stock: 0,
            purchase_price: 0,
          };
      const { data } = await api.post(endpoint, payload);
      toast.success(`"${data.name}" eklendi`);
      setNewForm({ code: "", name: "", color: "", unit: kind === "fabric" ? "kg" : "adet" });
      setAddOpen(false);
      // Reload list and auto-select the new one
      const { data: fresh } = await api.get(endpoint);
      setItems(Array.isArray(fresh) ? fresh : []);
      onChange([...value, data.id]);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2" data-testid={prefix}>
      <div className="flex items-center justify-between">
        <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">
          {label}
          {selected.length > 0 && (
            <span className="ml-2 text-blue-400">({selected.length} seçili)</span>
          )}
        </Label>
        <div className="flex items-center gap-2">
          {loading && <Loader2 className="w-3 h-3 text-slate-500 animate-spin" />}
          <button
            type="button"
            data-testid={`${prefix}-new-btn`}
            onClick={() => {
              setNewForm({ code: "", name: q || "", color: "", unit: kind === "fabric" ? "kg" : "adet" });
              setAddOpen(true);
            }}
            className="text-[11px] font-mono uppercase text-emerald-400 hover:text-emerald-300 flex items-center gap-0.5 transition-colors"
          >
            <Plus className="w-3 h-3" /> Yeni {kind === "fabric" ? "Kumaş" : "Aksesuar"}
          </button>
        </div>
      </div>

      {/* Chip list — selected items */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((it) => (
            <span
              key={it.id}
              className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 border border-blue-900/60 text-blue-200 bg-blue-950/40 rounded-sm font-mono"
              data-testid={`${prefix}-chip-${it.id}`}
            >
              <span className="text-blue-500">{it.code}</span>
              <span className="text-slate-300">·</span>
              <span className="max-w-[140px] truncate">{it.name}</span>
              <button
                type="button"
                onClick={() => remove(it.id)}
                className="hover:text-red-400 shrink-0"
                data-testid={`${prefix}-remove-${it.id}`}
                aria-label={`${it.name} kaldır`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search field */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none" />
        <Input
          data-testid={`${prefix}-search`}
          value={q}
          onFocus={() => setShowDropdown(true)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
          onChange={(e) => {
            setQ(e.target.value);
            setShowDropdown(true);
          }}
          placeholder={placeholder}
          className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 pl-9 h-9 text-xs"
        />

        {showDropdown && (
          <div
            className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto bg-slate-950 border border-slate-700 rounded-sm shadow-lg"
            data-testid={`${prefix}-dropdown`}
          >
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-slate-500">
                {q ? (
                  <>
                    "{q}" bulunamadı.
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setNewForm({ code: "", name: q, color: "", unit: kind === "fabric" ? "kg" : "adet" });
                        setAddOpen(true);
                      }}
                      className="ml-2 text-emerald-400 hover:text-emerald-300 underline"
                    >
                      Yeni ekle
                    </button>
                  </>
                ) : "Kayıt yok"}
              </div>
            ) : (
              filtered.map((it) => {
                const isSelected = value.includes(it.id);
                return (
                  <button
                    key={it.id}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      toggle(it.id);
                    }}
                    data-testid={`${prefix}-option-${it.id}`}
                    className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 border-b border-slate-800/50 last:border-0 transition-colors ${
                      isSelected
                        ? "bg-blue-950/40 text-blue-100"
                        : "hover:bg-slate-800/60 text-slate-200"
                    }`}
                  >
                    <span
                      className={`w-4 h-4 rounded-sm border shrink-0 flex items-center justify-center ${
                        isSelected
                          ? "bg-blue-500 border-blue-500"
                          : "border-slate-600 bg-slate-900"
                      }`}
                    >
                      {isSelected ? <Check className="w-3 h-3 text-white" strokeWidth={3} /> : <Plus className="w-3 h-3 text-slate-500" />}
                    </span>
                    <span className="font-mono text-blue-400 shrink-0">{it.code}</span>
                    <span className="text-slate-300 truncate flex-1">{it.name}</span>
                    {kind === "fabric" && it.color && (
                      <span className="text-[10px] text-slate-500 font-mono shrink-0">{it.color}</span>
                    )}
                    {kind === "accessory" && it.category && (
                      <span className="text-[10px] text-slate-500 font-mono shrink-0">{it.category}</span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="rounded-sm sm:max-w-md bg-slate-900 border-slate-800 text-slate-100">
          <DialogHeader>
            <DialogTitle>Yeni {kind === "fabric" ? "Kumaş" : "Aksesuar"} Ekle</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase font-mono text-slate-400">Ad *</Label>
              <Input
                autoFocus
                data-testid={`${prefix}-new-name`}
                value={newForm.name}
                onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter" && newForm.name.trim()) createNew(); }}
                placeholder={kind === "fabric" ? "Örn: Ripstop Siyah" : "Örn: 18 CM Fermuar"}
                className="bg-slate-950 border-slate-700 rounded-sm mt-1 focus-visible:ring-blue-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase font-mono text-slate-400">Kod (opsiyonel)</Label>
                <Input
                  data-testid={`${prefix}-new-code`}
                  value={newForm.code}
                  onChange={(e) => setNewForm({ ...newForm, code: e.target.value })}
                  placeholder="Auto"
                  className="bg-slate-950 border-slate-700 rounded-sm mt-1 focus-visible:ring-blue-500 font-mono"
                />
              </div>
              <div>
                <Label className="text-xs uppercase font-mono text-slate-400">Birim</Label>
                <Input
                  data-testid={`${prefix}-new-unit`}
                  value={newForm.unit}
                  onChange={(e) => setNewForm({ ...newForm, unit: e.target.value })}
                  placeholder={kind === "fabric" ? "kg / metre" : "adet"}
                  className="bg-slate-950 border-slate-700 rounded-sm mt-1 focus-visible:ring-blue-500 font-mono"
                />
              </div>
            </div>
            {kind === "fabric" && (
              <div>
                <Label className="text-xs uppercase font-mono text-slate-400">Renk (opsiyonel)</Label>
                <Input
                  data-testid={`${prefix}-new-color`}
                  value={newForm.color}
                  onChange={(e) => setNewForm({ ...newForm, color: e.target.value })}
                  placeholder="Örn: Siyah, Lacivert"
                  className="bg-slate-950 border-slate-700 rounded-sm mt-1 focus-visible:ring-blue-500"
                />
              </div>
            )}
            <div className="text-[11px] font-mono text-slate-500">
              Fiyat, stok ve diğer detayları daha sonra Stok Yönetimi'nden düzenleyebilirsiniz.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm">
              İptal
            </Button>
            <Button
              data-testid={`${prefix}-new-save`}
              onClick={createNew}
              disabled={saving || !newForm.name.trim()}
              className="bg-emerald-600 hover:bg-emerald-500 rounded-sm uppercase tracking-wider text-xs font-semibold"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
              Ekle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
